<script setup lang="ts">
import { ref, computed, onUnmounted, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/vue/outline";
import { useEventsStore } from "@/stores/modules/events/events";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import AgendaItem from "@/components/AgendaItem.vue";
import Birthday from "@/components/Birthday.vue";
import InformationText from "@/components/InformationText.vue";
import type { EventDocument } from "@kekkle/shared/types/documents/event-document/event-document";

const { locale } = useI18n();
const eventsStore = useEventsStore();
const groupsStore = useGroupsStore();

const events = computed(() => eventsStore.eventDocs);
const loadingInitial = computed(() => eventsStore.loadingEvents);
const fetchError = computed(() => eventsStore.fetchError);

type BirthdayEntry = {
  type: "birthday";
  uuid: string;
  birthdayDate: number;
  isToday: boolean;
};

type EventEntry = {
  type: "event";
  event: EventDocument;
};

type SectionItem = EventEntry | BirthdayEntry;

type MonthSection = {
  key: string;
  label: string;
  itemsBeforeToday: SectionItem[];
  itemsAfterToday: SectionItem[];
  isCurrentMonth: boolean;
};

function itemDayOfMonth(item: SectionItem): number {
  return item.type === "event"
    ? item.event.begin_timestamp.getDate()
    : item.birthdayDate;
}

function monthKey(year: number, month: number): string {
  return `${year}-${month}`;
}

function monthLabel(year: number, month: number): string {
  const date = new Date(year, month, 1);
  return new Intl.DateTimeFormat(locale.value, {
    year: "numeric",
    month: "long",
  }).format(date);
}

const sections = computed<MonthSection[]>(() => {
  const now = new Date();
  const todayMonth = now.getMonth();
  const todayDate = now.getDate();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Collect events by month key
  const eventsByKey = new Map<string, EventDocument[]>();
  for (const event of events.value) {
    const d = event.begin_timestamp;
    const key = monthKey(d.getFullYear(), d.getMonth());
    if (!eventsByKey.has(key)) {
      eventsByKey.set(key, []);
    }
    eventsByKey.get(key)!.push(event);
  }

  // Collect birthdays by month (birthdays repeat yearly, place in current year context)
  const birthdays = groupsStore.birthdays ?? [];
  const birthdaysByMonth = new Map<number, BirthdayEntry[]>();
  for (const b of birthdays) {
    const month = b.birthday.getMonth();
    if (!birthdaysByMonth.has(month)) {
      birthdaysByMonth.set(month, []);
    }
    const isToday =
      b.birthday.getMonth() === todayMonth &&
      b.birthday.getDate() === todayDate;
    birthdaysByMonth.get(month)!.push({
      type: "birthday",
      uuid: b.uuid,
      birthdayDate: b.birthday.getDate(),
      isToday,
    });
  }
  // Sort birthdays within each month by date
  for (const entries of birthdaysByMonth.values()) {
    entries.sort((a, b) => a.birthdayDate - b.birthdayDate);
  }

  // Determine month range — events are desc, so first = newest, last = oldest
  // Also include current month to always show it
  let minYear = currentYear;
  let minMonth = currentMonth;
  let maxYear = currentYear;
  let maxMonth = currentMonth;

  for (const event of events.value) {
    const d = event.begin_timestamp;
    const y = d.getFullYear();
    const m = d.getMonth();
    if (y < minYear || (y === minYear && m < minMonth)) {
      minYear = y;
      minMonth = m;
    }
    if (y > maxYear || (y === maxYear && m > maxMonth)) {
      maxYear = y;
      maxMonth = m;
    }
  }

  // Build sections for every month in range (descending)
  const result: MonthSection[] = [];
  let y = maxYear;
  let m = maxMonth;

  while (y > minYear || (y === minYear && m >= minMonth)) {
    const key = monthKey(y, m);
    const monthEvents = eventsByKey.get(key) ?? [];
    const monthBirthdays = birthdaysByMonth.get(m) ?? [];
    const isCurrentMonth = y === currentYear && m === currentMonth;

    const allItems: SectionItem[] = [];

    for (const event of monthEvents) {
      allItems.push({ type: "event", event });
    }

    if (y === currentYear) {
      for (const birthday of monthBirthdays) {
        allItems.push(birthday);
      }
    }

    // Sort items by day descending (newest first within the month)
    allItems.sort((a, b) => itemDayOfMonth(b) - itemDayOfMonth(a));

    // Split around today for the current month
    let itemsBeforeToday: SectionItem[];
    let itemsAfterToday: SectionItem[];

    if (isCurrentMonth) {
      itemsBeforeToday = allItems.filter(
        (item) => itemDayOfMonth(item) >= todayDate,
      );
      itemsAfterToday = allItems.filter(
        (item) => itemDayOfMonth(item) < todayDate,
      );
    } else {
      itemsBeforeToday = allItems;
      itemsAfterToday = [];
    }

    result.push({
      key,
      label: monthLabel(y, m),
      itemsBeforeToday,
      itemsAfterToday,
      isCurrentMonth,
    });

    m--;
    if (m < 0) {
      m = 11;
      y--;
    }
  }

  return result;
});

// Past event check
function isPastEvent(event: EventDocument): boolean {
  return event.end_timestamp < new Date();
}

// Today indicator ref + floating arrow
const todayIndicatorRef = ref<HTMLElement | HTMLElement[] | null>(null);
const todayDirection = ref<"above" | "below" | "visible">("visible");

function isTodayInSection(section: MonthSection): boolean {
  return section.isCurrentMonth;
}

function getTodayElement(): HTMLElement | null {
  const val = todayIndicatorRef.value;
  if (!val) return null;
  // ref inside v-for yields an array
  if (Array.isArray(val)) return val[0] ?? null;
  return val;
}

function updateTodayDirection(): void {
  const el = getTodayElement();
  if (!el) {
    todayDirection.value = "visible";
    return;
  }
  const rect = el.getBoundingClientRect();
  if (rect.bottom < 0) {
    todayDirection.value = "above";
  } else if (rect.top > window.innerHeight) {
    todayDirection.value = "below";
  } else {
    todayDirection.value = "visible";
  }
}

function scrollToToday(): void {
  getTodayElement()?.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });
}

// Scroll & today direction tracking
function handleScroll(): void {
  updateTodayDirection();
}

// When events change, update today direction
watch(events, () => {
  nextTick(() => updateTodayDirection());
});

// Lifecycle
eventsStore.subscribeToEvents();
window.addEventListener("scroll", handleScroll);

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<template>
  <div class="p-2">
    <InformationText
      v-if="fetchError && !loadingInitial"
      color="red"
      class="mb-2"
      :information="fetchError"
    />

    <!-- Loading initial -->
    <div v-if="loadingInitial" class="space-y-4">
      <div
        v-for="n in 3"
        :key="'skeleton-' + n"
        class="h-32 w-full animate-pulse rounded-lg border-2 bg-gray-200 dark:border-kekkle-dark-light dark:bg-kekkle-dark-light"
      />
    </div>

    <!-- Sections by month -->
    <template v-else>
      <div v-if="sections.length === 0">
        <p class="text-center dark:text-slate-200">
          {{ $t("pages.event.no_events_yet") }}
        </p>
      </div>

      <div v-for="section in sections" :key="section.key" class="mb-6">
        <!-- Month header -->
        <h2
          class="mb-3 text-sm font-semibold text-gray-500 capitalize dark:text-slate-400"
        >
          {{ section.label }}
        </h2>

        <!-- Empty month message -->
        <p
          v-if="
            section.itemsBeforeToday.length === 0 &&
            section.itemsAfterToday.length === 0 &&
            !section.isCurrentMonth
          "
          class="py-2 text-center text-sm text-gray-400 dark:text-slate-500"
        >
          {{ $t("pages.event.no_events_month") }}
        </p>

        <!-- Items before today (future / today's date, shown above the line) -->
        <div v-if="section.itemsBeforeToday.length > 0" class="space-y-4">
          <template
            v-for="item in section.itemsBeforeToday"
            :key="item.type === 'event' ? item.event._id : item.uuid"
          >
            <div
              v-if="item.type === 'event'"
              :class="isPastEvent(item.event) ? 'opacity-50 grayscale-20' : ''"
            >
              <AgendaItem :item="item.event" />
            </div>
            <Birthday
              v-else
              :uuid="item.uuid"
              :birthday-date="item.birthdayDate"
              :is-today="item.isToday"
            />
          </template>
        </div>

        <!-- Today indicator -->
        <div
          v-if="isTodayInSection(section)"
          ref="todayIndicatorRef"
          class="relative my-4 flex items-center"
        >
          <div
            class="h-3 w-3 shrink-0 rounded-full border-2 border-kekkle-blue bg-white dark:bg-kekkle-dark-dark"
          />
          <div class="h-0.5 flex-1 bg-kekkle-blue" />
          <span
            class="shrink-0 px-2 text-xs font-semibold text-kekkle-blue dark:text-kekkle-blue-light"
          >
            {{ $t("pages.event.today") }}
          </span>
          <div class="h-0.5 flex-1 bg-kekkle-blue" />
        </div>

        <!-- Items after today (past, shown below the line) -->
        <div v-if="section.itemsAfterToday.length > 0" class="space-y-4">
          <template
            v-for="item in section.itemsAfterToday"
            :key="item.type === 'event' ? item.event._id : item.uuid"
          >
            <div
              v-if="item.type === 'event'"
              :class="isPastEvent(item.event) ? 'opacity-50 grayscale-20' : ''"
            >
              <AgendaItem :item="item.event" />
            </div>
            <Birthday
              v-else
              :uuid="item.uuid"
              :birthday-date="item.birthdayDate"
              :is-today="item.isToday"
            />
          </template>
        </div>
      </div>
    </template>

    <!-- Floating "scroll to today" button -->
    <Transition name="fade">
      <button
        v-if="todayDirection !== 'visible' && !loadingInitial"
        class="fixed bottom-20 left-0 z-20 ml-5 rounded-lg border-2 border-kekkle-blue-dark bg-kekkle-blue-light p-1 shadow-blue-hard-sm transition-all duration-500 ease-in-out hover:translate-y-1 hover:bg-kekkle-blue hover:shadow-none"
        @click="scrollToToday"
      >
        <div class="flex items-center justify-center gap-x-2 px-2 py-1">
          <p class="text-md h-min text-kekkle-blue-dark">
            {{ $t("pages.event.today") }}
          </p>
          <ChevronUpIcon
            v-if="todayDirection === 'above'"
            class="w-5 text-kekkle-blue-dark"
          />
          <ChevronDownIcon v-else class="w-5 text-kekkle-blue-dark" />
        </div>
      </button>
    </Transition>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
